import { useCallback, useRef, useState } from 'react';

import {
    ACTIVITY_1011_CONFIG_KEY,
    ACTIVITY_1011_CONFIG_MODULE,
} from '@/constants/activity-1011';
import {
    buildActivity1011ConfigValue,
    createEmptyConfig,
    mergeSectionIntoConfig,
    parseActivity1011ConfigValue,
} from '@/pages/activity-1011/task-config/utils';
import { patchConfigsKeyActivity } from '@/services/generated/config';
import {
    isServiceConfigVersionConflict,
    serviceConfigList,
    type ServiceConfigItem,
} from '@/services/service-config';

import type { Activity1011Config, Activity1011ConfigSection } from '@/types/activity-1011';

const toConfigRecord = (data: unknown): ServiceConfigItem | null => {
    if (!data || typeof data !== 'object') return null;
    return data as ServiceConfigItem;
};

export const useActivity1011Config = () => {
    const [loading, setLoading] = useState(false);
    const [savingSection, setSavingSection] = useState<Activity1011ConfigSection | null>(null);
    const [configRecord, setConfigRecord] = useState<ServiceConfigItem | null>(null);
    const [config, setConfig] = useState<Activity1011Config>(createEmptyConfig());

    const versionRef = useRef<number | undefined>(undefined);
    /** 服务端最新整包，分区保存时以此为 merge base */
    const serverConfigRef = useRef<Activity1011Config>(createEmptyConfig());

    const findConfigRecord = useCallback(async (): Promise<ServiceConfigItem | null> => {
        let page = 1;
        const size = 100;

        while (true) {
            const { data } = await serviceConfigList({ page, size });
            if (data.code !== 200) {
                throw new Error(data.msg);
            }

            const matched =
                data.data?.find(
                    (item) =>
                        item.key === ACTIVITY_1011_CONFIG_KEY && item.module === ACTIVITY_1011_CONFIG_MODULE,
                ) ?? null;

            if (matched) {
                return matched;
            }

            const total = data.meta?.total ?? 0;
            const fetched = page * size;
            if (fetched >= total || !(data.data?.length)) {
                return null;
            }

            page += 1;
        }
    }, []);

    const applyRecord = useCallback((record: ServiceConfigItem | null) => {
        const parsed = parseActivity1011ConfigValue(record?.value);
        setConfigRecord(record);
        versionRef.current = record?.version;
        serverConfigRef.current = parsed;
        setConfig(parsed);
    }, []);

    const loadConfig = useCallback(async () => {
        setLoading(true);
        try {
            const record = await findConfigRecord();
            applyRecord(record);
        } finally {
            setLoading(false);
        }
    }, [applyRecord, findConfigRecord]);

    const persistValue = useCallback(
        async (next: Activity1011Config) => {
            const value = buildActivity1011ConfigValue(next);

            const { data } = await patchConfigsKeyActivity({
                value,
                version: versionRef.current ?? 0,
            } as Parameters<typeof patchConfigsKeyActivity>[0]);

            if (isServiceConfigVersionConflict(data)) {
                return { conflict: true as const };
            }

            if (data.code !== 200) {
                throw new Error(data.msg);
            }

            const record = toConfigRecord(data.data);
            if (record) {
                applyRecord(record);
            } else {
                if (data.data?.version !== undefined) {
                    versionRef.current = data.data.version;
                }
                const parsed = parseActivity1011ConfigValue(value);
                serverConfigRef.current = parsed;
                setConfig(parsed);
            }

            return { conflict: false as const };
        },
        [applyRecord],
    );

    /** 分区保存：把该分区字段 merge 进服务端整包后 PATCH */
    const saveSection = useCallback(
        async (section: Activity1011ConfigSection, sectionDraft: Activity1011Config) => {
            setSavingSection(section);
            try {
                const merged = mergeSectionIntoConfig(serverConfigRef.current, section, sectionDraft);
                return await persistValue(merged);
            } finally {
                setSavingSection(null);
            }
        },
        [persistValue],
    );

    return {
        loading,
        savingSection,
        configRecord,
        config,
        setConfig,
        loadConfig,
        saveSection,
    };
};
